<script setup lang="ts">
import PageHeader from '@/components/page-header.vue'
import PageContent from '@/components/page-content.vue'
import ContentLoader from "@/components/content-loader.vue";
import { faDownload, faCircleInfo, faPenToSquare, faCheck, faEye } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import ButtonSecondary from '@/components/button-secondary.vue';
import ButtonHighlighted from '@/components/button-highlighted.vue';
import DropdownItem from '@/components/dropdown-item.vue';
import InputSelect from "@/components/input-select.vue";
import { filterQueries, mainQueries } from '@/features/tariffs/queries';
import { useQuery, queryOptions } from '@tanstack/vue-query';
import { fetchTariffs } from '@/features/tariffs/api/fetchTariffs'
import { computed, ref, watch, reactive } from 'vue';
import Tabs from '@/components/tabs-teal.vue'
import splashImage from '@/assets/tariffs/dashboard/splash.svg'
import HeroSplash from '@/components/hero-splash.vue'
import { Season, ChargeName, PSO } from '@/features/tariffs/constants'
import type { Tariffs } from '@/features/tariffs/api/fetchTariffs';
import { faHandPointUp } from '@fortawesome/pro-solid-svg-icons';
import ModalTouProfile from './modal-tou-profie.vue';
import ModalTariffSource from './modal-tariff-source.vue';
import ModalVariantConfirmation from '@/components/modal/modal-variant-confirmation.vue';
import { useApproveTariffs } from '@/features/tariffs/mutations';
import { useModalState } from '@/shared/composables/useModalState';
import useNotifications from '@/composables/notifications';
import { useDebounce } from '@vueuse/core';
import DataTablePagination from '@/components/data-table-pagination.vue'
import VerticallyCentered from "@/components/vertically-centered.vue";
import { useAuthenticatedUser } from '@/shared/composables/useAuthenticatedUser'
import {
  usePageQueryParameter,
  usePageSizeQueryParameter,
} from '@/composables/query-parameters'

interface PeriodValue {
  rate: number | null
  oldRate: number | null
  changePercent: number | null
  isActive: boolean
  bandId: number
  label?: string
}

interface TariffRow {
  tariffId: number
  groupKey: string | null
  providerId: number
  providerName: string
  tariffName: string
  source: 'AMMP' | 'UNIFII'
  sourceDetail: string | null
  sourceNotes: string | null
  transZone: string | null
  voltage: string | null
  isTimeOfUse: boolean
  isApproved: boolean
  yearOfTariff: string

  blocks: { label: string }[]
  blocksW: PeriodValue[]
  blocksS: PeriodValue[]

  peakW: PeriodValue[]
  standardW: PeriodValue[]
  offPeakW: PeriodValue[]
  peakS: PeriodValue[]
  standardS: PeriodValue[]
  offPeakS: PeriodValue[]

  basicChargeW: PeriodValue[]
  demandChargeW: PeriodValue[]
  accessChargeW: PeriodValue[]
  serviceChargeW: PeriodValue[]
  capacityChargeW: PeriodValue[]
  reactiveChargeW: PeriodValue[]
  legacyChargeW: PeriodValue[]
  networkDemandChargeW: PeriodValue[]
  generationChargeW: PeriodValue[]
  electrificationChargeW: PeriodValue[]
  affordabilityChargeW: PeriodValue[]
  ancillaryChargeW: PeriodValue[]
  creditReverseChargeW: PeriodValue[]
  fixedMonthlyChargeW: PeriodValue[]
  fixedDailyChargeW: PeriodValue[]
  networkSurchargeChargeW: PeriodValue[]
  subsidyChargeW: PeriodValue[]
  reactiveServiceChargeW: PeriodValue[]
  additionalEnergyChargeW: PeriodValue[]

  basicChargeS: PeriodValue[]
  demandChargeS: PeriodValue[]
  accessChargeS: PeriodValue[]
  serviceChargeS: PeriodValue[]
  capacityChargeS: PeriodValue[]
  reactiveChargeS: PeriodValue[]
  legacyChargeS: PeriodValue[]
  networkDemandChargeS: PeriodValue[]
  generationChargeS: PeriodValue[]
  electrificationChargeS: PeriodValue[]
  affordabilityChargeS: PeriodValue[]
  ancillaryChargeS: PeriodValue[]
  creditReverseChargeS: PeriodValue[]
  fixedMonthlyChargeS: PeriodValue[]
  fixedDailyChargeS: PeriodValue[]
  networkSurchargeChargeS: PeriodValue[]
  subsidyChargeS: PeriodValue[]
  reactiveServiceChargeS: PeriodValue[]
  additionalEnergyChargeS: PeriodValue[]
}

function makePeriod(period: any, label?: string): PeriodValue {
  return {
    rate: period.RateInCents,
    oldRate: period.OldRateInCents ?? null,
    changePercent: period.RateChangePercent ?? null,
    isActive: period.IsActive,
    bandId: period.Id,
    label,
  }
}

// Multiple bands of the same fixed-charge-type tariff (e.g. one row per consumption
// band) come back from the API as separate records sharing a GroupKey. Merge them into
// a single record with a combined Periods list so they render — and get approved — as
// one tariff instead of one row per band.
function commonNamePrefix(names: string[]): string {
  let prefix = names[0]
  for (const name of names.slice(1)) {
    let i = 0
    while (i < prefix.length && i < name.length && prefix[i] === name[i]) i++
    prefix = prefix.slice(0, i)
  }
  return prefix.replace(/[\s\-–—(]+$/, '').trim()
}

// "2025/2026" -> 2026, "2026" -> 2026 — mirrors the backend's TariffYearHelper.GetLatestYear.
function latestYearNumber(yearOfTariff: string): number {
  const matches = yearOfTariff.match(/\d+/g)
  return matches ? Math.max(...matches.map(Number)) : 0
}

function groupTariffsByGroupKey(tariffs: Tariffs, keepOnlyLatestYear: boolean): Tariffs {
  const groups = new Map<string, { tariff: Tariffs[number]; names: string[] }>()
  const result: Tariffs[number][] = []

  for (const tariff of tariffs) {
    if (!tariff.GroupKey) {
      result.push(tariff)
      continue
    }
    const group = groups.get(tariff.GroupKey)
    if (group) {
      group.tariff.Periods.push(...tariff.Periods)
      group.names.push(tariff.Name)
    } else {
      const merged = { ...tariff, Periods: [...tariff.Periods] }
      groups.set(tariff.GroupKey, { tariff: merged, names: [tariff.Name] })
      result.push(merged)
    }
  }

  for (const group of groups.values()) {
    if (group.names.length > 1) {
      const prefix = commonNamePrefix(group.names)
      if (prefix.length >= 4) group.tariff.Name = prefix
    }

    // A GroupKey's pending records can span more than one fiscal year - e.g. AMMP
    // publishes a partial/unbanded update for the newest year while last year's fully
    // banded data is still pending too. Approving supersedes older years with the
    // newest one, so only the newest year's periods are relevant to review here;
    // otherwise a fully-banded prior year and a flat, unbanded new year get blended
    // into one row's band list.
    if (keepOnlyLatestYear) {
      const years = group.tariff.Periods.map(p => latestYearNumber(p.YearOfTariff))
      const latestYear = Math.max(...years)
      if (new Set(years).size > 1) {
        group.tariff.Periods = group.tariff.Periods.filter(p => latestYearNumber(p.YearOfTariff) === latestYear)
      }
    }
  }

  return result
}

function mapTariffs(tariffs: Tariffs | undefined, isCompare: boolean): TariffRow[] {
  if (!Array.isArray(tariffs)) return []

  return groupTariffsByGroupKey(tariffs, isCompare).map(tariff => {
    const row: TariffRow = {
      tariffId: isCompare ? (Number(tariff.ExternalId?.split(',')[0]) || 0) : tariff.Id,
      groupKey: tariff.GroupKey ?? null,
      providerId: tariff.UtilityProviderId,
      providerName: tariff.UtilityProviderName,
      tariffName: tariff.Name,
      source: tariff.ExternalId ? 'AMMP' : 'UNIFII',
      sourceDetail: tariff.TariffSourceData ?? null,
      sourceNotes: tariff.Notes ?? null,
      transZone: tariff.TransZone,
      voltage: tariff.Voltage,
      isTimeOfUse: tariff.IsTimeOfUse,
      isApproved: tariff.IsApproved,
      yearOfTariff: '',

      blocks: [], blocksW: [], blocksS: [],
      peakW: [], standardW: [], offPeakW: [],
      peakS: [], standardS: [], offPeakS: [],

      basicChargeW: [], demandChargeW: [], accessChargeW: [], serviceChargeW: [],
      capacityChargeW: [], reactiveChargeW: [], legacyChargeW: [],
      networkDemandChargeW: [], generationChargeW: [], electrificationChargeW: [],
      affordabilityChargeW: [], ancillaryChargeW: [], creditReverseChargeW: [],
      fixedMonthlyChargeW: [], fixedDailyChargeW: [],
      networkSurchargeChargeW: [], subsidyChargeW: [], reactiveServiceChargeW: [],
      additionalEnergyChargeW: [],

      basicChargeS: [], demandChargeS: [], accessChargeS: [], serviceChargeS: [],
      capacityChargeS: [], reactiveChargeS: [], legacyChargeS: [],
      networkDemandChargeS: [], generationChargeS: [], electrificationChargeS: [],
      affordabilityChargeS: [], ancillaryChargeS: [], creditReverseChargeS: [],
      fixedMonthlyChargeS: [], fixedDailyChargeS: [],
      networkSurchargeChargeS: [], subsidyChargeS: [], reactiveServiceChargeS: [],
      additionalEnergyChargeS: [],
    }

    for (const period of tariff.Periods) {
      row.yearOfTariff = period.YearOfTariff
      const isWinter = period.Season === Season.Winter
      const isSummer = period.Season === Season.Summer
      const isNone   = period.Season === Season.None

      // ── Flat-rate energy blocks ──────────────────────────────────────────────
      if (period.ChargeName === ChargeName.Energy && !tariff.IsTimeOfUse) {
        const blockLabel = period.StartOfPeriod || period.EndOfPeriod
          ? period.EndOfPeriod != null
            ? `${period.StartOfPeriod} - ${period.EndOfPeriod}`
            : `${period.StartOfPeriod}+`
          : ''
        const band = makePeriod(period, blockLabel)

        if (isSummer) {
          row.blocksS.push(band)
          if (band.rate && !row.blocks.some(b => b.label === blockLabel))
            row.blocks.push({ label: blockLabel })
          continue
        }
        if (isWinter) {
          row.blocksW.push(band)
          if (band.rate && !row.blocks.some(b => b.label === blockLabel))
            row.blocks.push({ label: blockLabel })
          continue
        }
        // isNone — push to both but only add label once
        if (isNone) {
          const bandW = makePeriod(period, blockLabel)
          const bandS = makePeriod(period, blockLabel)
          row.blocksW.push(bandW)
          row.blocksS.push(bandS)
          if (band.rate && !row.blocks.some(b => b.label === blockLabel))
            row.blocks.push({ label: blockLabel })
          continue
        }
      }

      const band = makePeriod(period)

      // ── Fixed charges ────────────────────────────────────────────────────────
      const chargeMap: Record<string, keyof TariffRow> = {
        [ChargeName.Basic]:           isWinter || isNone ? 'basicChargeW'          : 'basicChargeS',
        [ChargeName.Demand]:          isWinter || isNone ? 'demandChargeW'         : 'demandChargeS',
        [ChargeName.Access]:          isWinter || isNone ? 'accessChargeW'         : 'accessChargeS',
        [ChargeName.Service]:         isWinter || isNone ? 'serviceChargeW'        : 'serviceChargeS',
        [ChargeName.Capacity]:        isWinter || isNone ? 'capacityChargeW'       : 'capacityChargeS',
        [ChargeName.Reactive]:        isWinter || isNone ? 'reactiveChargeW'       : 'reactiveChargeS',
        [ChargeName.Legacy]:          isWinter || isNone ? 'legacyChargeW'         : 'legacyChargeS',
        [ChargeName.NetworkDemand]:   isWinter || isNone ? 'networkDemandChargeW'  : 'networkDemandChargeS',
        [ChargeName.Generation]:      isWinter || isNone ? 'generationChargeW'     : 'generationChargeS',
        [ChargeName.Electrification]: isWinter || isNone ? 'electrificationChargeW': 'electrificationChargeS',
        [ChargeName.Affordability]:   isWinter || isNone ? 'affordabilityChargeW'  : 'affordabilityChargeS',
        [ChargeName.Ancillary]:       isWinter || isNone ? 'ancillaryChargeW'      : 'ancillaryChargeS',
        [ChargeName.CreditRev]:       isWinter || isNone ? 'creditReverseChargeW'  : 'creditReverseChargeS',
        [ChargeName.FixedDaily]:      isWinter || isNone ? 'fixedDailyChargeW'     : 'fixedDailyChargeS',
        [ChargeName.FixedMonthly]:    isWinter || isNone ? 'fixedMonthlyChargeW'   : 'fixedMonthlyChargeS',
        [ChargeName.NetworkSurcharge]: isWinter || isNone ? 'networkSurchargeChargeW' : 'networkSurchargeChargeS',
        [ChargeName.Subsidy]:         isWinter || isNone ? 'subsidyChargeW'        : 'subsidyChargeS',
        [ChargeName.ReactiveService]: isWinter || isNone ? 'reactiveServiceChargeW' : 'reactiveServiceChargeS',
        [ChargeName.AdditionalEnergy]: isWinter || isNone ? 'additionalEnergyChargeW' : 'additionalEnergyChargeS',
      }

      const targetKey = chargeMap[period.ChargeName]
      if (targetKey) {
        const arr = row[targetKey] as PeriodValue[]
        // Fixed charges (e.g. a flat monthly fee) are often repeated identically across
        // every consumption band of a grouped tariff — only show each distinct value once.
        if (!arr.some(b => b.rate === band.rate && b.oldRate === band.oldRate))
          arr.push(band)
        continue
      }

      // ── TOU energy — only push to the season it belongs to ──────────────────
      if (tariff.IsTimeOfUse) {
        // isNone means it applies to both seasons — push to both explicitly
        const pushW = isWinter || isNone
        const pushS = isSummer || isNone

        if (pushW) {
          if (period.TimeOfUse === PSO.Peak)     row.peakW.push(band)
          if (period.TimeOfUse === PSO.Standard) row.standardW.push(band)
          if (period.TimeOfUse === PSO.OffPeak)  row.offPeakW.push(band)
        }
        if (pushS) {
          if (period.TimeOfUse === PSO.Peak)     row.peakS.push(band)
          if (period.TimeOfUse === PSO.Standard) row.standardS.push(band)
          if (period.TimeOfUse === PSO.OffPeak)  row.offPeakS.push(band)
        }
      }
    }

    return row
  })
}

type PeriodValueArrayKeys = {
  [K in keyof TariffRow]: TariffRow[K] extends PeriodValue[] ? K : never
}[keyof TariffRow]

function rowHasNoOldValues(row: Pick<TariffRow, PeriodValueArrayKeys>) {
  for (const val of Object.values(row)) {
    if (!Array.isArray(val)) continue
    if ((val as PeriodValue[]).some(x => x.oldRate !== null)) return false
  }
  return true
}

// ─── Selection ────────────────────────────────────────────────────────────────

const selectedRecords = ref<Array<string|number>>([])
const hasSelection = computed(() => selectedRecords.value.length > 0)

function isSelected(groupKey: string|number|null) {
  return groupKey != null && selectedRecords.value.includes(groupKey)
}

function toggleSelection(groupKey: string|number|null) {
  if (groupKey == null) return
  const i = selectedRecords.value.indexOf(groupKey)
  if (i === -1) selectedRecords.value.push(groupKey)
  else          selectedRecords.value.splice(i, 1)
}

// Add/remove the current page's rows without discarding selections from other pages.
function toggleAll(event: MouseEvent) {
  const pageKeys = tariffRows.value
    .map(r => r.groupKey ?? r.tariffId)
    .filter((v): v is string | number => v != null)
  if ((event.target as HTMLInputElement).checked) {
    const toAdd = pageKeys.filter(k => !selectedRecords.value.includes(k))
    selectedRecords.value = [...selectedRecords.value, ...toAdd]
  } else {
    selectedRecords.value = selectedRecords.value.filter(k => !pageKeys.includes(k))
  }
}

const allCurrentPageSelected = computed(() =>
  tariffRows.value.length > 0 &&
  tariffRows.value.every(r => isSelected(r.groupKey ?? r.tariffId))
)

// ─── Approval confirmation modal ───────────────────────────────────────────────

const approveConfirm  = reactive(useModalState())
const approveAllMode  = ref(false)

function openApproveConfirm(all: boolean) {
  if (!all && !hasSelection.value) return
  approveAllMode.value = all
  approveConfirm.open()
}

// ─── Filter state ─────────────────────────────────────────────────────────────

const page     = usePageQueryParameter()
const pageSize = usePageSizeQueryParameter()
const regions            = ref<number[]>([])
const utilityProviders   = ref<number[]>([])
const tariffIds          = ref<number[]>([])
const compareProviders   = ref<string[]>([])
const compareTariffNames = ref<string[]>([])

const { addSuccess, addError, addWarning } = useNotifications()
const { data: regionData }          = useQuery(filterQueries.fetchRegion())
const { data: utilityProviderData } = useQuery(filterQueries.fetchProviders(regions))
const { data: tariffNamesData }     = useQuery(filterQueries.fetchTariffNames(utilityProviders))

const debouncedIds = useDebounce(tariffIds, 1000)

// When all available tariff names are selected, the province/provider filters already
// cover the full set — sending every ID would bloat the URL for no benefit.
const effectiveIds = computed(() => {
  const all = tariffNamesData.value ?? []
  const selected = debouncedIds.value
  return all.length > 0 && selected.length === all.length ? [] : selected
})

const { data: yearsData }           = useQuery(filterQueries.fetchTariffYears(effectiveIds, regions, utilityProviders))
const { data: authenticatedUser, isLoading: isLoadingAuthenticatedUser } = useAuthenticatedUser()
const years        = computed(() => ['Compare & Approve', ...(yearsData.value?.map(String) ?? [])])
const selectedYear = ref('')
const touProfiles  = reactive(useModalState())
const tariffSourceModal = reactive(useModalState<TariffRow>())

watch(years, (val) => {
  if (val.length > 0) {
    selectedYear.value = val.length > 1 ? val[1] : val[0]
    page.value = 1
  }
}, { immediate: true })

watch(regions,          () => { utilityProviders.value = []; tariffIds.value = [] }, { immediate: true })
watch(utilityProviders, () => { tariffIds.value = [] },                              { immediate: true })
watch(selectedYear,     () => { selectedRecords.value = []; page.value = 1 },        { immediate: true })
watch(compareProviders, () => {
  page.value = 1
  if (compareTariffNames.value.length) {
    const available = new Set(compareTariffOptions.value.map(t => t.Name))
    compareTariffNames.value = compareTariffNames.value.filter(t => available.has(t))
  }
})
watch(compareTariffNames, () => { page.value = 1 })
const isCompare    = computed(() => selectedYear.value.startsWith('Compare'))

const { data: tariffsData, isFetching: isFetchingMain } = useQuery(
  mainQueries.fetchTariffs({
    ids: effectiveIds,
    yearOfTariff: selectedYear,
    page,
    pageSize,
    filter: '',
    sorting: [],
    provinceIds: regions,
    providerIds: utilityProviders,
  })
)

// Compare tab: load the full dataset once — client-side filter/paginate below.
const { data: compareOptionsResult, isFetching: isFetchingCompare } = useQuery(
  queryOptions({
    queryKey: ['tariffs', 'compareOptions'] as const,
    queryFn: () => fetchTariffs([], 'Compare & Approve', 1, 5000),
    enabled: () => isCompare.value,
  })
)

const isFetching = computed(() => isFetchingMain.value || isFetchingCompare.value)

const { mutateAsync, isPending } = useApproveTariffs(
  computed(() => authenticatedUser.value?.id)
)
const tariffs = computed(() => tariffsData.value?.data ?? [])

// ─── Mapped rows ──────────────────────────────────────────────────────────────

// All compare records (no name filter) — source for dropdowns and "all" operations.
const compareAllRows = computed(() => mapTariffs(compareOptionsResult.value?.data, true))

// Compare tab applies provider/tariff name filters purely client-side for instant response.
const compareFilteredRows = computed(() => {
  if (!isCompare.value) return []
  return compareAllRows.value.filter(r =>
    (!compareProviders.value.length   || compareProviders.value.includes(r.providerName)) &&
    (!compareTariffNames.value.length || compareTariffNames.value.includes(r.tariffName))
  )
})

const compareAllCount = computed(() => compareFilteredRows.value.length)

const tariffRows = computed<TariffRow[]>(() => {
  if (isCompare.value) {
    const p = page.value
    const s = pageSize.value ?? 50
    return compareFilteredRows.value.slice((p - 1) * s, p * s)
  }
  return mapTariffs(tariffs.value, false)
})

const meta = computed(() => isCompare.value
  ? { TotalCount: compareFilteredRows.value.length, Page: page.value, PageSize: pageSize.value ?? 50 }
  : tariffsData.value?.meta
)

const compareProviderOptions = computed(() =>
  [...new Map(compareAllRows.value.map(r => [r.providerName, { Name: r.providerName }])).values()]
)
const compareTariffOptions = computed(() => {
  const base = compareProviders.value.length
    ? compareAllRows.value.filter(r => compareProviders.value.includes(r.providerName))
    : compareAllRows.value
  return [...new Map(base.map(r => [r.tariffName, { Name: r.tariffName }])).values()]
})

// ─── CSV ──────────────────────────────────────────────────────────────────────

function bandsToCsv(bands: PeriodValue[], prefix: string, compare: boolean) {
  return bands.map((b, i) => ({
    [`${prefix}_rate_${i}`]: b.rate,
    ...(compare ? { [`${prefix}_old_${i}`]: b.oldRate, [`${prefix}_pct_${i}`]: b.changePercent } : {}),
  })).reduce((a, b) => ({ ...a, ...b }), {})
}

function rowsToCsv(records: TariffRow[]) {
  if (!records.length) return

  const compare = isCompare.value
  const flatForCsv = records.map(r => ({
    TariffId:          r.tariffId,
    Provider:          r.providerName,
    Tariff:            r.tariffName,
    Source:            r.source,
    TransZone:         r.transZone,
    Voltage:           r.voltage,
    Year:              r.yearOfTariff,
    BlocksWinter:      r.blocks.map(b => b.label).join('|'),
    BlocksWinterRates: r.blocksW.map(b => b.rate).join('|'),
    BlocksSummer:      r.blocks.map(b => b.label).join('|'),
    BlocksSummerRates: r.blocksS.map(b => b.rate).join('|'),
    ...bandsToCsv(r.peakW,     'PeakW',     compare),
    ...bandsToCsv(r.standardW, 'StandardW', compare),
    ...bandsToCsv(r.offPeakW,  'OffPeakW',  compare),
    ...bandsToCsv(r.peakS,     'PeakS',     compare),
    ...bandsToCsv(r.standardS, 'StandardS', compare),
    ...bandsToCsv(r.offPeakS,  'OffPeakS',  compare),
    ...bandsToCsv(r.basicChargeW,           'BasicChargeW',           compare),
    ...bandsToCsv(r.demandChargeW,          'DemandChargeW',          compare),
    ...bandsToCsv(r.accessChargeW,          'AccessChargeW',          compare),
    ...bandsToCsv(r.serviceChargeW,         'ServiceChargeW',         compare),
    ...bandsToCsv(r.capacityChargeW,        'CapacityChargeW',        compare),
    ...bandsToCsv(r.reactiveChargeW,        'ReactiveChargeW',        compare),
    ...bandsToCsv(r.legacyChargeW,          'LegacyChargeW',          compare),
    ...bandsToCsv(r.networkDemandChargeW,   'NetworkDemandChargeW',   compare),
    ...bandsToCsv(r.generationChargeW,      'GenerationChargeW',      compare),
    ...bandsToCsv(r.electrificationChargeW, 'ElectrificationChargeW', compare),
    ...bandsToCsv(r.affordabilityChargeW,   'AffordabilityChargeW',   compare),
    ...bandsToCsv(r.ancillaryChargeW,       'AncillaryChargeW',       compare),
    ...bandsToCsv(r.creditReverseChargeW,   'CreditReverseChargeW',   compare),
    ...bandsToCsv(r.networkSurchargeChargeW, 'NetworkSurchargeChargeW', compare),
    ...bandsToCsv(r.subsidyChargeW,          'SubsidyChargeW',          compare),
    ...bandsToCsv(r.reactiveServiceChargeW,  'ReactiveServiceChargeW',  compare),
    ...bandsToCsv(r.additionalEnergyChargeW, 'AdditionalEnergyChargeW', compare),
    ...bandsToCsv(r.basicChargeS,           'BasicChargeS',           compare),
    ...bandsToCsv(r.demandChargeS,          'DemandChargeS',          compare),
    ...bandsToCsv(r.accessChargeS,          'AccessChargeS',          compare),
    ...bandsToCsv(r.serviceChargeS,         'ServiceChargeS',         compare),
    ...bandsToCsv(r.capacityChargeS,        'CapacityChargeS',        compare),
    ...bandsToCsv(r.reactiveChargeS,        'ReactiveChargeS',        compare),
    ...bandsToCsv(r.legacyChargeS,          'LegacyChargeS',          compare),
    ...bandsToCsv(r.networkDemandChargeS,   'NetworkDemandChargeS',   compare),
    ...bandsToCsv(r.generationChargeS,      'GenerationChargeS',      compare),
    ...bandsToCsv(r.electrificationChargeS, 'ElectrificationChargeS', compare),
    ...bandsToCsv(r.affordabilityChargeS,   'AffordabilityChargeS',   compare),
    ...bandsToCsv(r.ancillaryChargeS,       'AncillaryChargeS',       compare),
    ...bandsToCsv(r.creditReverseChargeS,   'CreditReverseChargeS',   compare),
    ...bandsToCsv(r.networkSurchargeChargeS, 'NetworkSurchargeChargeS', compare),
    ...bandsToCsv(r.subsidyChargeS,          'SubsidyChargeS',          compare),
    ...bandsToCsv(r.reactiveServiceChargeS,  'ReactiveServiceChargeS',  compare),
    ...bandsToCsv(r.additionalEnergyChargeS, 'AdditionalEnergyChargeS', compare),
  }))

  // Collect headers from ALL rows so columns from TOU tariffs aren't dropped when
  // the first row happens to be a block tariff (and vice versa).
  const allKeys = new Set<string>()
  flatForCsv.forEach(r => Object.keys(r).forEach(k => allKeys.add(k)))
  const headers = Array.from(allKeys)
  const csv = [
    headers.join(','),
    ...flatForCsv.map(r => headers.map(h => JSON.stringify(r[h as keyof typeof r] ?? '')).join(','))
  ].join('\r\n')

  const filename = (tariffNamesData.value ?? [])
    .filter(t => tariffIds.value.includes(t.Id))
    .map(t => t.Name).join('-')
    .replace(/[^a-z0-9-_]/gi, '_').slice(0, 100) + '-' + Date.now() + '.csv'

  const link = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
    download: filename,
    style: 'visibility:hidden',
  })
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function downloadSelected() {
  const pool = isCompare.value ? compareFilteredRows.value : tariffRows.value
  rowsToCsv(pool.filter(r => selectedRecords.value.includes(r.groupKey ?? r.tariffId as string | number)))
}

function downloadAll() {
  rowsToCsv(isCompare.value ? compareFilteredRows.value : tariffRows.value)
}

// ─── Approve ──────────────────────────────────────────────────────────────────

async function approveTariffs(keys: Array<string | number>) {
  try {
    const res = await mutateAsync(keys)
    selectedRecords.value = selectedRecords.value.filter(k => !keys.includes(k))

    if ((res?.Rejected?.length ?? 0) > 0) {
      addWarning({
        title: `${res?.Approved ?? 0} approved, ${res?.Rejected?.length ?? 0} could not be approved.`,
        message: res?.Rejected?.join('\n') ?? '',
        autoHide: false
      })
    } else {
      page.value = 1
      compareProviders.value   = []
      compareTariffNames.value = []
      addSuccess({ title: `${res?.Approved ?? 0} tariffs approved successfully.` })
    }
  } catch (error: any) {
    const data = error?.data
    if (data?.Errors?.length) {
      addError({
        title: 'We encountered some issues while trying to approve.',
        message: data.Errors.map((e: { Message: string }) => e.Message).join('\n'),
        autoHide: false
      })
    } else if (data?.Data?.Rejected?.length) {
      addWarning({
        title: `${data.Data.Approved ?? 0} approved, ${data.Data.Rejected.length} could not be approved.`,
        message: data.Data.Rejected.join('\n'),
        autoHide: false
      })
    } else {
      addError({ title: 'Approval failed.' })
    }
  }
}

async function confirmApprove() {
  approveConfirm.close()
  const keys = approveAllMode.value
    ? compareFilteredRows.value
        .map(r => r.groupKey ?? r.tariffId)
        .filter((v): v is string | number => v != null)
    : [...selectedRecords.value]
  await approveTariffs(keys)
}
</script>

<template>
  <PageHeader>
    <div>
      <div class="d-inline-block text-nowrap">
        <label class="me-2">Region</label>
        <InputSelect name="region" :items="regionData ?? []" multiple item-value="Id" item-text="Name"
          v-model:selected="regions" :display-limit="1" inline select-all />
      </div>
      <div class="d-inline-block text-nowrap ms-4">
        <label class="me-2">Utility Provider</label>
        <InputSelect name="utilityProvider" :items="utilityProviderData ?? []" multiple item-value="Id" item-text="Name"
          v-model:selected="utilityProviders" :display-limit="1" inline select-all />
      </div>
      <div class="d-inline-block text-nowrap ms-4">
        <label class="me-2">Tariff</label>
        <InputSelect :searchable="true" name="tariff" :items="tariffNamesData ?? []" multiple item-value="Id"
          item-text="Name" v-model:selected="tariffIds" :display-limit="1" inline select-all />
      </div>
    </div>
    <div>
      <ButtonSecondary :icon="faPenToSquare" @click="touProfiles.open()">TOU Profile</ButtonSecondary>
    </div>
  </PageHeader>

  <PageContent>
    <VerticallyCentered v-if="years.length == 1 && tariffRows.length === 0 && !isFetching && !isLoadingAuthenticatedUser">
      <HeroSplash title="Customize Your View!"
        sub-title="Select options from the dropdowns to populate the screen." :image-url="splashImage" />
    </VerticallyCentered>

    <Tabs v-else :tabs="years" v-model="selectedYear" />

    <PageContent class="d-flex flex-column h-100">
      <ContentLoader :loading="isFetching || isPending || isLoadingAuthenticatedUser" :variant="tariffRows.length === 0 ? 'overlay' : 'table'">

        <div v-if="isCompare && tariffRows.length > 0" class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-2 ms-4">
            <FontAwesomeIcon :icon="faCircleInfo" class="fa-2x text-yellow" />
            <span>These filters only apply to the Compare &amp; Approve tab:</span>
          </div>
          <div class="d-inline-block text-nowrap ms-4">
            <label class="me-2">Utility Provider</label>
            <InputSelect name="compareProvider" :items="compareProviderOptions" multiple item-value="Name"
              item-text="Name" v-model:selected="compareProviders" :display-limit="1" inline />
          </div>
          <div class="d-inline-block text-nowrap ms-4">
            <label class="me-2">Tariff</label>
            <InputSelect :searchable="true" name="compareTariff" :items="compareTariffOptions" multiple item-value="Name"
              item-text="Name" v-model:selected="compareTariffNames" :display-limit="1" inline />
          </div>
        </div>

        <div class="table-responsive" v-if="tariffRows.length > 0">
          <table class="table w-100 table-hover u-data-table table-sm">
            <thead>
              <tr class="no-bottom-border">
                <th colspan="7" class="border-right"></th>
                <th :colspan="isCompare ? 3 : 2" class="text-center border-right">Block High</th>
                <th :colspan="isCompare ? 2 : 2" class="text-center border-right">Block Low</th>
                <th :colspan="isCompare ? 6 : 3" class="text-center border-right">High Demand</th>
                <th :colspan="isCompare ? 6 : 3" class="text-center border-right">Low Demand</th>
                <th :colspan="isCompare ? 38 : 19" class="text-center border-right">High Demand Charges</th>
                <th :colspan="isCompare ? 38 : 19" class="text-center border-right">Low Demand Charges</th>
              </tr>
              <tr class="text-nowrap">
                <th>
                  <input type="checkbox" class="form-check-input" ref="$selectAll"
                    :checked="allCurrentPageSelected" @click="toggleAll"
                    v-tooltip="selectedRecords.length > 0 ? `${selectedRecords.length} selected` : undefined" />
                </th>
                <th>Utility Provider</th>
                <th>Tariff</th>
                <th>Year</th>
                <th>Source</th>
                <th>Trans Zone</th>
                <th class="border-right">Voltage</th>

                <!-- Block Winter -->
                <th>Block</th>
                <th v-if="isCompare">Rate<br>Old</th>
                <th class="border-right">Rate<span v-if="isCompare"><br>New</span></th>

                <!-- Block Summer -->
                <th v-if="isCompare">Rate<br>Old</th>
                <th class="border-right">Rate<span v-if="isCompare"><br>New</span></th>

                <!-- TOU Winter -->
                <template v-for="col in ['Peak', 'Standard', 'Off-Peak']" :key="'W-' + col">
                  <th v-if="isCompare">{{ col }}<br>Old</th>
                  <th :class="col === 'Off-Peak' ? 'border-right' : ''">{{ col }}<span v-if="isCompare"><br>New</span></th>
                </template>

                <!-- TOU Summer -->
                <template v-for="col in ['Peak', 'Standard', 'Off-Peak']" :key="'S-' + col">
                  <th v-if="isCompare">{{ col }}<br>Old</th>
                  <th :class="col === 'Off-Peak' ? 'border-right' : ''">{{ col }}<span v-if="isCompare"><br>New</span></th>
                </template>

                <!-- Winter Charges -->
                <template v-for="col in ['Basic', 'Demand', 'Access', 'Service', 'Capacity', 'Reactive', 'Legacy', 'Network Demand', 'Generation', 'Electrification', 'Affordability', 'Ancillary', 'Credit Reverse', 'Fixed Monthly', 'Fixed Daily', 'Network Surcharge', 'Subsidy', 'Reactive Service', 'Additional Energy']" :key="'CW-' + col">
                  <th v-if="isCompare">{{ col }}<br>Old</th>
                  <th :class="col === 'Additional Energy' ? 'border-right' : ''">{{ col }}<span v-if="isCompare"><br>New</span></th>
                </template>

                <!-- Summer Charges -->
                <template v-for="col in ['Basic', 'Demand', 'Access', 'Service', 'Capacity', 'Reactive', 'Legacy', 'Network Demand', 'Generation', 'Electrification', 'Affordability', 'Ancillary', 'Credit Reverse', 'Fixed Monthly', 'Fixed Daily', 'Network Surcharge', 'Subsidy', 'Reactive Service', 'Additional Energy']" :key="'CS-' + col">
                  <th v-if="isCompare">{{ col }}<br>Old</th>
                  <th :class="col === 'Additional Energy' ? 'border-right' : ''">{{ col }}<span v-if="isCompare"><br>New</span></th>
                </template>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in tariffRows" :key="row.groupKey ?? row.tariffId" class="text-nowrap align-top"
                :class="{ 'row-new': isCompare && rowHasNoOldValues(row) }">
                <td>
                  <input type="checkbox" class="form-check-input"
                    :checked="isSelected(row.groupKey?? row.tariffId)"
                    @change="toggleSelection(row.groupKey?? row.tariffId)" />
                </td>
                <td>{{ row.providerName }}</td>
                <td>{{ row.tariffName }}</td>
                <td>{{ row.yearOfTariff }}</td>
                <td>
                  <FontAwesomeIcon :icon="faEye" role="button" v-tooltip="'View source'"
                    @click="tariffSourceModal.open(row)" />
                </td>
                <td>{{ row.transZone }}</td>
                <td class="border-right">{{ row.voltage }}</td>

                <!-- Block label -->
                <td>
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell">{{ b.label || '—' }}</div>
                  <div v-if="!row.blocks.length">—</div>
                </td>

                <!-- Block Winter rates — aligned to blocks -->
                <td v-if="isCompare">
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell">
                    {{ row.blocksW.find(w => w.label === b.label)?.oldRate ?? '—' }}
                  </div>
                </td>
                <td class="border-right">
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell"
                    :class="bandClass(row.blocksW.find(w => w.label === b.label))"
                    v-tooltip="bandTooltip(row.blocksW.find(w => w.label === b.label), isCompare)">
                    {{ row.blocksW.find(w => w.label === b.label)?.rate ?? '—' }}
                  </div>
                  <div v-if="!row.blocks.length">—</div>
                </td>

                <!-- Block Summer rates — aligned to blocks -->
                <td v-if="isCompare">
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell">
                    {{ row.blocksS.find(s => s.label === b.label)?.oldRate ?? '—' }}
                  </div>
                </td>
                <td class="border-right">
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell"
                    :class="bandClass(row.blocksS.find(s => s.label === b.label))"
                    v-tooltip="bandTooltip(row.blocksS.find(s => s.label === b.label), isCompare)">
                    {{ row.blocksS.find(s => s.label === b.label)?.rate ?? '—' }}
                  </div>
                  <div v-if="!row.blocks.length">—</div>
                </td>

                <!-- TOU Winter -->
                <template v-for="bands in [row.peakW, row.standardW, row.offPeakW]" :key="'tw-' + bands.length">
                  <td v-if="isCompare" class="text-center">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell">{{ b.oldRate }}</div>
                  </td>
                  <td class="text-center" :class="bands === row.offPeakW ? 'border-right' : ''">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell"
                      :class="bandClass(b)" v-tooltip="bandTooltip(b, isCompare)">{{ b.rate }}</div>
                    <div v-if="!bands.length">—</div>
                  </td>
                </template>

                <!-- TOU Summer -->
                <template v-for="bands in [row.peakS, row.standardS, row.offPeakS]" :key="'ts-' + bands.length">
                  <td v-if="isCompare" class="text-center">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell">{{ b.oldRate }}</div>
                  </td>
                  <td class="text-center" :class="bands === row.offPeakS ? 'border-right' : ''">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell"
                      :class="bandClass(b)" v-tooltip="bandTooltip(b, isCompare)">{{ b.rate }}</div>
                    <div v-if="!bands.length">—</div>
                  </td>
                </template>

                <!-- Winter Charges -->
                <template v-for="(bands, label) in {
                  'basicChargeW': row.basicChargeW, 'demandChargeW': row.demandChargeW,
                  'accessChargeW': row.accessChargeW, 'serviceChargeW': row.serviceChargeW,
                  'capacityChargeW': row.capacityChargeW, 'reactiveChargeW': row.reactiveChargeW,
                  'legacyChargeW': row.legacyChargeW, 'networkDemandChargeW': row.networkDemandChargeW,
                  'generationChargeW': row.generationChargeW, 'electrificationChargeW': row.electrificationChargeW,
                  'affordabilityChargeW': row.affordabilityChargeW, 'ancillaryChargeW': row.ancillaryChargeW,
                  'creditReverseChargeW': row.creditReverseChargeW, 'fixedMonthlyChargeW': row.fixedMonthlyChargeW,
                  'fixedDailyChargeW': row.fixedDailyChargeW,
                  'networkSurchargeChargeW': row.networkSurchargeChargeW, 'subsidyChargeW': row.subsidyChargeW,
                  'reactiveServiceChargeW': row.reactiveServiceChargeW, 'additionalEnergyChargeW': row.additionalEnergyChargeW,
                }" :key="label">
                  <td v-if="isCompare" class="text-center">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell">{{ b.oldRate }}</div>
                  </td>
                  <td class="text-center" :class="label === 'additionalEnergyChargeW' ? 'border-right' : ''">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell"
                      :class="bandClass(b)" v-tooltip="bandTooltip(b, isCompare)">{{ b.rate }}</div>
                    <div v-if="!bands.length">—</div>
                  </td>
                </template>

                <!-- Summer Charges -->
                <template v-for="(bands, label) in {
                  'basicChargeS': row.basicChargeS, 'demandChargeS': row.demandChargeS,
                  'accessChargeS': row.accessChargeS, 'serviceChargeS': row.serviceChargeS,
                  'capacityChargeS': row.capacityChargeS, 'reactiveChargeS': row.reactiveChargeS,
                  'legacyChargeS': row.legacyChargeS, 'networkDemandChargeS': row.networkDemandChargeS,
                  'generationChargeS': row.generationChargeS, 'electrificationChargeS': row.electrificationChargeS,
                  'affordabilityChargeS': row.affordabilityChargeS, 'ancillaryChargeS': row.ancillaryChargeS,
                  'creditReverseChargeS': row.creditReverseChargeS, 'fixedMonthlyChargeS': row.fixedMonthlyChargeS,
                  'fixedDailyChargeS': row.fixedDailyChargeS,
                  'networkSurchargeChargeS': row.networkSurchargeChargeS, 'subsidyChargeS': row.subsidyChargeS,
                  'reactiveServiceChargeS': row.reactiveServiceChargeS, 'additionalEnergyChargeS': row.additionalEnergyChargeS,
                }" :key="label">
                  <td v-if="isCompare" class="text-center">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell">{{ b.oldRate }}</div>
                  </td>
                  <td class="text-center" :class="label === 'additionalEnergyChargeS' ? 'border-right' : ''">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell"
                      :class="bandClass(b)" v-tooltip="bandTooltip(b, isCompare)">{{ b.rate }}</div>
                    <div v-if="!bands.length">—</div>
                  </td>
                </template>
              </tr>

              <tr v-if="tariffRows.length === 0">
                <td colspan="40" class="text-center text-muted">
                  We couldn't find any tariffs that match your search.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <DataTablePagination
          v-if="meta && tariffRows.length > 0"
          :total-records="meta?.TotalCount"
          :page="meta?.Page"
          @update:page="page = $event"
          :page-size="meta?.PageSize"
          @update:page-size="pageSize = $event"
        />
        <VerticallyCentered v-else-if="tariffRows.length === 0">
          <HeroSplash title="Customize Your View!"
            sub-title="Select options from the dropdowns to populate the screen." :image-url="splashImage" />
        </VerticallyCentered>
      </ContentLoader>

      <div class="mt-4 py-3 px-4 bg-white d-flex align-items-center gap-2" style="position: sticky; bottom: 0; z-index: 10;"
        v-if="tariffRows.length > 0">

        <!-- Download split button -->
        <ButtonSecondary class="me-3" :icon="faDownload" menu-align="left">
          Download
          <template #menu>
            <DropdownItem @click="downloadSelected" :disabled="!hasSelection">
              Download selected
            </DropdownItem>
            <DropdownItem @click="downloadAll">
              Download all Compare &amp; Approve records
            </DropdownItem>
          </template>
        </ButtonSecondary>

        <!-- Approve split button (compare tab only) -->
        <ButtonHighlighted v-if="isCompare" :icon="faHandPointUp" menu-align="left">
          Approve ({{ selectedRecords.length }})
          <template #menu>
            <DropdownItem @click="openApproveConfirm(false)" :disabled="!hasSelection">
              Approve selected
            </DropdownItem>
            <DropdownItem @click="openApproveConfirm(true)">
              Approve all Compare &amp; Approve records
            </DropdownItem>
          </template>
        </ButtonHighlighted>
      </div>
    </PageContent>

    <ModalTouProfile v-if="touProfiles.isOpen" @close="touProfiles.close()" />

    <ModalTariffSource
      v-if="tariffSourceModal.isOpen"
      :source="tariffSourceModal.state.sourceDetail"
      :notes="tariffSourceModal.state.sourceNotes"
      @close="tariffSourceModal.close()"
    />

    <!-- Approval confirmation modal -->
    <ModalVariantConfirmation
      v-if="approveConfirm.isOpen"
      title="Confirm Approval"
      confirm-text="Approve"
      :confirm-icon="faCheck"
      @confirm="confirmApprove"
      @reject="approveConfirm.close()"
    >
      Are you sure you want to approve
      <strong>
        {{ approveAllMode
          ? `all ${compareAllCount} record(s)`
          : `${selectedRecords.length} selected record(s)` }}
      </strong>?
      This action cannot be undone.
    </ModalVariantConfirmation>
  </PageContent>
</template>

<script lang="ts">
function bandClass(band: { changePercent: number | null } | undefined) {
  if (band?.changePercent == null) return {}
  return {
    'text-red':   band.changePercent > 15,
    'text-green': band.changePercent < 15,
  }
}

function bandTooltip(band: { changePercent: number | null } | undefined, isCompare: boolean) {
  if (!isCompare || band?.changePercent == null) return undefined
  return { placement: 'bottom', text: `Increase: ${band.changePercent}%` }
}
</script>

<style lang="scss" scoped>
.no-bottom-border th { border-bottom: none; }
.border-right        { border-right: 1px solid #D1D1D1; }
.text-red            { color: #FD5624; }
.text-green          { color: #28A09C; }

.row-new td { font-weight: 600; }

.band-cell {
  line-height: 1.6;
  padding: 1px 0;

  &:not(:last-child) {
    border-bottom: 1px dashed #e0e0e0;
  }
}
</style>